// hooks/useStore.js — Global state management via localStorage + React state
//
// ── Content flow ──────────────────────────────────────────────────────────────
// Admin edits   → saved to   webstudio_draft   (immediately)
// Save/Publish  → copies draft to  webstudio_published
// Public site   → reads from webstudio_published (fallback: defaultContent)
// Admin panel   → reads from webstudio_draft    (fallback: published → defaultContent)

import { useState, useEffect, useCallback } from "react";
import { DEFAULT_CONTENT } from "../data/defaultContent";
import { loadPublishedContent, savePublishedContent } from "../utils/firebase";

const DRAFT_KEY     = "webstudio_draft";
const PUBLISHED_KEY = "webstudio_published";
const THEME_KEY     = "webstudio_theme";
const LANG_KEY      = "webstudio_lang";
const AUTH_KEY      = "webstudio_auth";

// ── helpers ───────────────────────────────────────────────────────────────────
const readLS = (key, fallback) => {
  try {
    const s = localStorage.getItem(key);
    if (!s) return fallback;
    const parsed = JSON.parse(s);
    return fallback ? { ...fallback, ...parsed } : parsed;
  } catch { return fallback; }
};

const writeLS = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
};

// ── usePublicContent — از Firebase می‌خونه، localStorage cache ───────────────
export function usePublicContent() {
  const [loading, setLoading] = useState(() => !localStorage.getItem(PUBLISHED_KEY));
  const [content, setContent] = useState(() => {
    const cached = readLS(PUBLISHED_KEY, null);
    return cached ? { ...DEFAULT_CONTENT, ...cached } : DEFAULT_CONTENT;
  });

  useEffect(() => {
    loadPublishedContent().then((data) => {
      if (data) {
        const merged = { ...DEFAULT_CONTENT, ...data };
        setContent(merged);
        writeLS(PUBLISHED_KEY, merged);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return { content, loading };
}


// ── useDraftContent — Admin draft می‌خونه و می‌نویسه ──────────────────────────
export function useDraftContent() {
  const [draft, setDraft] = useState(() => {
    const published = readLS(PUBLISHED_KEY, DEFAULT_CONTENT);
    return readLS(DRAFT_KEY, published);
  });

  const [hasUnsaved, setHasUnsaved] = useState(() => {
    try {
      const d = localStorage.getItem(DRAFT_KEY);
      const p = localStorage.getItem(PUBLISHED_KEY);
      return d !== null && d !== p;
    } catch { return false; }
  });

  const updateDraft = useCallback((updater) => {
    setDraft((prev) => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      writeLS(DRAFT_KEY, next);
      try {
        const p = localStorage.getItem(PUBLISHED_KEY);
        setHasUnsaved(JSON.stringify(next) !== p);
      } catch { setHasUnsaved(true); }
      return next;
    });
  }, []);

  const publishDraft = useCallback(async () => {
    setDraft((current) => {
      writeLS(PUBLISHED_KEY, current);
      writeLS(DRAFT_KEY, current);
      setHasUnsaved(false);
      // ذخیره در Firebase
      savePublishedContent(current).catch((err) =>
        console.error("Firebase publish error:", err)
      );
      return current;
    });
  }, []);

  const discardDraft = useCallback(() => {
    const published = readLS(PUBLISHED_KEY, DEFAULT_CONTENT);
    setDraft(published);
    writeLS(DRAFT_KEY, published);
    setHasUnsaved(false);
  }, []);

  const resetContent = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
    localStorage.removeItem(PUBLISHED_KEY);
    setDraft(DEFAULT_CONTENT);
    setHasUnsaved(false);
  }, []);

  return { draft, updateDraft, publishDraft, discardDraft, resetContent, hasUnsaved };
}

// ── useTheme ──────────────────────────────────────────────────────────────────
export function useTheme() {
  const [dark, setDarkState] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_KEY);
      if (stored !== null) return stored === "dark";
    } catch {}
    return true; // default: dark
  });

  const setDark = useCallback((val) => {
    const v = typeof val === "function" ? val(dark) : val;
    setDarkState(v);
    try { localStorage.setItem(THEME_KEY, v ? "dark" : "light"); } catch {}
  }, [dark]);

  return { dark, setDark };
}

// ── useLang ───────────────────────────────────────────────────────────────────
export function useLang() {
  const [lang, setLangState] = useState(() => {
    try {
      // اول از URL بخون
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get("lang");
      if (urlLang === "en" || urlLang === "es") return urlLang;
      // بعد از localStorage
      const stored = localStorage.getItem(LANG_KEY);
      if (stored) return stored;
    } catch {}
    return null;
  });

  useEffect(() => {
    if (lang !== null) return;
    const detect = async () => {
      try {
        const res = await fetch("https://get.geojs.io/v1/ip/country.json");
        const data = await res.json();
        setLangState(data.country === "ES" ? "es" : "en");
      } catch { setLangState("en"); }
    };
    detect();
  }, [lang]);

  const setLang = useCallback((val) => {
    setLangState(val);
    try {
      localStorage.setItem(LANG_KEY, val);
      // آپدیت URL
      const url = new URL(window.location.href);
      url.searchParams.set("lang", val);
      window.history.replaceState({}, "", url.toString());
    } catch {}
  }, []);

  return { lang: lang ?? "en", setLang };
}

// ── useAuth ───────────────────────────────────────────────────────────────────
export function useAuth() {
  const [isAdmin, setIsAdmin] = useState(() => {
    try { return sessionStorage.getItem(AUTH_KEY) === "true"; } catch { return false; }
  });

  const login = useCallback((username, password) => {
    const validUser = process.env.REACT_APP_ADMIN_USER || "admin";
    const validPass = process.env.REACT_APP_ADMIN_PASS || "SuperSecret123";
    if (username === validUser && password === validPass) {
      sessionStorage.setItem(AUTH_KEY, "true");
      setIsAdmin(true);
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(AUTH_KEY);
    setIsAdmin(false);
  }, []);

  return { isAdmin, login, logout };
}
